/**
 * Toast notification system.
 *
 * Provides ToastProvider, useToast hook, and Toast component.
 * Supports variants: info, success, warning, error.
 * Restrained styling — minimal color, clean borders.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

type ToastVariant = 'info' | 'success' | 'warning' | 'error';

interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration: number;
}

interface ToastContextValue {
  toast: (options: Omit<ToastItem, 'id' | 'duration'> & { duration?: number }) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const variantStyles: Record<ToastVariant, string> = {
  info: 'bg-surface text-foreground',
  success: 'bg-surface text-foreground',
  warning: 'bg-surface text-foreground',
  error: 'bg-surface text-foreground',
};

const variantAccent: Record<ToastVariant, string> = {
  info: 'bg-neutral-400',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
};

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback<ToastContextValue['toast']>(
    ({ duration = 4000, ...options }) => {
      const id = `toast-${++toastCounter}`;
      const item: ToastItem = { id, duration, ...options };
      setToasts((prev) => [...prev, item]);

      const timer = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((timer) => clearTimeout(timer));
      map.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          role="region"
          aria-label="Notifications"
          className="fixed bottom-6 right-6 z-50 flex flex-col gap-2"
        >
          {toasts.map((item) => (
            <div
              key={item.id}
              role="alert"
              className={cn(
                'flex flex-col gap-1 rounded-lg border border-border shadow-lg',
                'animate-slide-up min-w-[320px] max-w-md overflow-hidden',
                variantStyles[item.variant],
              )}
            >
              <div className="flex items-stretch">
                <div className={cn('w-1 shrink-0', variantAccent[item.variant])} />
                <div className="flex items-start justify-between gap-3 p-4 flex-1">
                  <div className="flex flex-col gap-0.5">
                    <p className="text-sm font-semibold tracking-tight">{item.title}</p>
                    {item.description && (
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => dismiss(item.id)}
                    className="text-neutral-400 hover:text-foreground transition-colors duration-fast"
                    aria-label="Dismiss notification"
                  >
                    <span className="text-lg leading-none">&times;</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
